import React, { useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { B2bAfspraak } from '../services/lopendeTrajectenApi';

/**
 * Outlook-achtige agenda voor B2B-afspraken (dag / werkweek / week / maand).
 * Gedeeld door de opdrachtgever (Trajecten → Agenda) en de professional.
 * Puur presentatie: data en acties komen van de pagina.
 */

export type AgendaWeergave = 'dag' | 'werkweek' | 'week' | 'maand';

const WEERGAVEN: Array<{ key: AgendaWeergave; label: string }> = [
  { key: 'dag', label: 'Dag' },
  { key: 'werkweek', label: 'Werkweek' },
  { key: 'week', label: 'Week' },
  { key: 'maand', label: 'Maand' },
];

const START_UUR = 7;
const EIND_UUR = 21;
const UUR_HOOGTE = 48; // px
const UREN = Array.from({ length: EIND_UUR - START_UUR }, (_, i) => START_UUR + i);

/** Kleur volgt de levensfase: wacht op bevestiging → bevestigd → uren ingediend → gefiatteerd. */
export function afspraakStijl(a: B2bAfspraak): { klasse: string; label: string } {
  if (a.status === 'GEANNULEERD') {
    return { klasse: 'bg-gray-100 border-gray-300 text-gray-400 line-through', label: a.bevestiging_status === 'GEWEIGERD' ? 'Geweigerd' : 'Geannuleerd' };
  }
  if (a.status === 'GEFIATTEERD' || a.status === 'GEFACTUREERD') return { klasse: 'bg-green-100 border-green-500 text-green-900', label: 'Gefiatteerd' };
  if (a.status === 'UITGEVOERD') return { klasse: 'bg-orange-100 border-orange-500 text-orange-900', label: 'Uren ingediend' };
  if (a.status === 'AFGEWEZEN') return { klasse: 'bg-red-100 border-red-500 text-red-900', label: 'Uren afgewezen' };
  if (a.bevestiging_status === 'WACHT') return { klasse: 'bg-amber-50 border-amber-500 border-dashed text-amber-900', label: 'Wacht op bevestiging' };
  return { klasse: 'bg-blue-100 border-blue-600 text-blue-900', label: 'Bevestigd' };
}

interface Geplaatst {
  afspraak: B2bAfspraak;
  top: number;
  hoogte: number;
  kolom: number;
  kolommen: number;
}

/** Overlappende afspraken naast elkaar, zoals Outlook. */
function plaatsOpDag(afspraken: B2bAfspraak[], dag: Date): Geplaatst[] {
  const dagStart = startOfDay(dag).getTime();
  const items = afspraken
    .map((a) => {
      const s = Math.max((new Date(a.start_tijd).getTime() - dagStart) / 3600000, START_UUR);
      const e = Math.min((new Date(a.eind_tijd).getTime() - dagStart) / 3600000, EIND_UUR);
      return { a, s, e: Math.max(e, s + 0.5) };
    })
    .sort((x, y) => x.s - y.s || y.e - x.e);

  const resultaat: Geplaatst[] = [];
  let cluster: Array<{ a: B2bAfspraak; s: number; e: number; kolom: number }> = [];
  let clusterEind = -1;
  const sluitCluster = () => {
    const kolommen = Math.max(1, ...cluster.map((c) => c.kolom + 1));
    for (const c of cluster) {
      resultaat.push({ afspraak: c.a, top: (c.s - START_UUR) * UUR_HOOGTE, hoogte: (c.e - c.s) * UUR_HOOGTE, kolom: c.kolom, kolommen });
    }
    cluster = [];
  };
  for (const it of items) {
    if (cluster.length && it.s >= clusterEind) sluitCluster();
    const bezet = new Set(cluster.filter((c) => c.e > it.s).map((c) => c.kolom));
    let kolom = 0;
    while (bezet.has(kolom)) kolom++;
    cluster.push({ ...it, kolom });
    clusterEind = Math.max(clusterEind, it.e);
  }
  if (cluster.length) sluitCluster();
  return resultaat;
}

interface B2bAgendaProps {
  afspraken: B2bAfspraak[];
  /** Tweede regel in het blokje (professional resp. organisatie) */
  ondertitel: (a: B2bAfspraak) => string;
  onAfspraakClick: (a: B2bAfspraak) => void;
  /** Klik op een leeg tijdvak → nieuwe afspraak met voorgevulde starttijd */
  onSlotClick?: (start: Date) => void;
  geselecteerdId?: string | null;
}

export const B2bAgenda: React.FC<B2bAgendaProps> = ({ afspraken, ondertitel, onAfspraakClick, onSlotClick, geselecteerdId }) => {
  const [weergave, setWeergave] = useState<AgendaWeergave>('werkweek');
  const [anker, setAnker] = useState(() => new Date());
  const vandaag = new Date();

  const dagen = useMemo(() => {
    if (weergave === 'dag') return [startOfDay(anker)];
    const ma = startOfWeek(anker, { weekStartsOn: 1 });
    return Array.from({ length: weergave === 'werkweek' ? 5 : 7 }, (_, i) => addDays(ma, i));
  }, [weergave, anker]);

  const maandDagen = useMemo(() => {
    if (weergave !== 'maand') return [];
    const van = startOfWeek(startOfMonth(anker), { weekStartsOn: 1 });
    const tot = endOfWeek(endOfMonth(anker), { weekStartsOn: 1 });
    const lijst: Date[] = [];
    for (let dag = van; dag <= tot; dag = addDays(dag, 1)) lijst.push(dag);
    return lijst;
  }, [weergave, anker]);

  const blader = (richting: 1 | -1) => {
    if (weergave === 'maand') setAnker(addMonths(anker, richting));
    else if (weergave === 'dag') setAnker(addDays(anker, richting));
    else setAnker(addWeeks(anker, richting));
  };

  const titel =
    weergave === 'maand'
      ? format(anker, 'MMMM yyyy', { locale: nl })
      : weergave === 'dag'
      ? format(anker, 'EEEE d MMMM yyyy', { locale: nl })
      : `${format(dagen[0], 'd MMM', { locale: nl })} – ${format(dagen[dagen.length - 1], 'd MMM yyyy', { locale: nl })}`;

  const opDag = (dag: Date) => afspraken.filter((a) => isSameDay(new Date(a.start_tijd), dag));

  const slotKlik = (dag: Date, e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSlotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const halveUren = Math.floor(((e.clientY - rect.top) / UUR_HOOGTE) * 2);
    const start = new Date(startOfDay(dag));
    start.setMinutes(START_UUR * 60 + halveUren * 30);
    onSlotClick(start);
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 flex flex-col">
      {/* Werkbalk */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-200">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setAnker(new Date())} className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50">
            Vandaag
          </button>
          <button type="button" onClick={() => blader(-1)} className="p-1.5 rounded-md hover:bg-gray-100" aria-label="Vorige">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={() => blader(1)} className="p-1.5 rounded-md hover:bg-gray-100" aria-label="Volgende">
            <ChevronRight className="w-5 h-5" />
          </button>
          <h2 className="ml-2 text-base font-semibold text-gray-900 capitalize">{titel}</h2>
        </div>
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          {WEERGAVEN.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWeergave(w.key)}
              className={`px-3 py-1.5 text-sm font-medium ${weergave === w.key ? 'bg-navy-900 text-white shadow-[inset_0_-3px_0_#E5A425]' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {weergave === 'maand' ? (
        <div>
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
            {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((dag) => (
              <div key={dag} className="px-2 py-1.5">{dag}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {maandDagen.map((dag) => {
              const lijst = opDag(dag).sort((a, b) => a.start_tijd.localeCompare(b.start_tijd));
              return (
                <div
                  key={dag.toISOString()}
                  onClick={() => {
                    setAnker(dag);
                    setWeergave('dag');
                  }}
                  className={`min-h-[96px] border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-gray-50 ${isSameMonth(dag, anker) ? '' : 'bg-gray-50/60 text-gray-400'}`}
                >
                  <div className={`text-xs mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isSameDay(dag, vandaag) ? 'bg-[#12284A] text-white font-semibold' : ''}`}>
                    {format(dag, 'd')}
                  </div>
                  {lijst.slice(0, 3).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAfspraakClick(a);
                      }}
                      className={`block w-full text-left truncate text-[11px] leading-4 px-1 mb-0.5 rounded border-l-4 ${afspraakStijl(a).klasse}`}
                    >
                      {format(new Date(a.start_tijd), 'HH:mm')} {a.titel}
                    </button>
                  ))}
                  {lijst.length > 3 && <div className="text-[11px] text-gray-500 px-1">+{lijst.length - 3} meer</div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="overflow-auto max-h-[640px]">
          <div className="min-w-[560px]">
            {/* Dagkoppen */}
            <div className="flex sticky top-0 z-20 bg-white border-b border-gray-200">
              <div className="w-14 flex-shrink-0" />
              {dagen.map((dag) => (
                <div key={dag.toISOString()} className="flex-1 px-2 py-1.5 border-l border-gray-100">
                  <div className="text-xs uppercase text-gray-500">{format(dag, 'EEE', { locale: nl })}</div>
                  <div className={`text-lg font-semibold ${isSameDay(dag, vandaag) ? 'text-[#12284A]' : 'text-gray-900'}`}>{format(dag, 'd')}</div>
                </div>
              ))}
            </div>
            {/* Tijdraster */}
            <div className="flex">
              <div className="w-14 flex-shrink-0">
                {UREN.map((u) => (
                  <div key={u} style={{ height: UUR_HOOGTE }} className="text-[11px] text-gray-400 text-right pr-2 -mt-1.5 pt-0">
                    {String(u).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              {dagen.map((dag) => (
                <div
                  key={dag.toISOString()}
                  onClick={(e) => slotKlik(dag, e)}
                  className={`flex-1 relative border-l border-gray-100 ${onSlotClick ? 'cursor-cell' : ''} ${isSameDay(dag, vandaag) ? 'bg-blue-50/30' : ''}`}
                  style={{ height: UREN.length * UUR_HOOGTE }}
                >
                  {UREN.map((u) => (
                    <div key={u} style={{ height: UUR_HOOGTE }} className="border-b border-gray-100 pointer-events-none">
                      <div className="border-b border-dashed border-gray-50" style={{ height: UUR_HOOGTE / 2 }} />
                    </div>
                  ))}
                  {isSameDay(dag, vandaag) && vandaag.getHours() >= START_UUR && vandaag.getHours() < EIND_UUR && (
                    <div
                      className="absolute left-0 right-0 border-t-2 border-red-500 z-10 pointer-events-none"
                      style={{ top: (vandaag.getHours() + vandaag.getMinutes() / 60 - START_UUR) * UUR_HOOGTE }}
                    />
                  )}
                  {plaatsOpDag(opDag(dag), dag).map(({ afspraak: a, top, hoogte, kolom, kolommen }) => {
                    const stijl = afspraakStijl(a);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAfspraakClick(a);
                        }}
                        title={`${a.titel} — ${stijl.label}`}
                        className={`absolute text-left rounded border-l-4 px-1.5 py-0.5 overflow-hidden text-xs shadow-sm hover:shadow ${stijl.klasse} ${geselecteerdId === a.id ? 'ring-2 ring-[#12284A]' : ''}`}
                        style={{ top: top + 1, height: Math.max(hoogte - 2, 20), left: `calc(${(kolom / kolommen) * 100}% + 2px)`, width: `calc(${100 / kolommen}% - 4px)` }}
                      >
                        <div className="font-semibold truncate">{a.titel}</div>
                        <div className="truncate opacity-80">
                          {format(new Date(a.start_tijd), 'HH:mm')}–{format(new Date(a.eind_tijd), 'HH:mm')} · {ondertitel(a)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Legenda */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-3 py-2 border-t border-gray-200 text-xs text-gray-600">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-50 border border-dashed border-amber-500" /> Wacht op bevestiging</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-600" /> Bevestigd</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-orange-100 border border-orange-500" /> Uren ingediend</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-100 border border-green-500" /> Gefiatteerd → maandfactuur</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-300" /> Geweigerd / geannuleerd</span>
      </div>
    </div>
  );
};
